import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { footerSections, logoUrl, navLinks } from "@/lib/site-data";
import { Linkedin, Instagram, Phone } from "lucide-react";

export const SiteLayout = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <div className="relative min-h-screen bg-[#071A2C] text-[#D9E1E8]">
      <div className="pointer-events-none absolute inset-0 hero-radial" />

      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#071A2C]/80 shadow-[0_10px_30px_rgba(0,0,0,0.35)] backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 md:px-10">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <img
              src={logoUrl}
              alt="The AI Matters logo"
              className="h-10 w-10 rounded-full glow-ring"
              data-testid="header-logo-image"
            />
            <span className="text-sm font-semibold uppercase tracking-[0.2em]" data-testid="header-brand-text">
              The AI Matters
            </span>
          </Link>

          <nav className="hidden items-center gap-6 md:flex" data-testid="desktop-navigation">
            {navLinks.map((link) => (
              <NavLink
                key={link.id}
                to={link.to}
                data-testid={`header-nav-${link.id}-link`}
                className={({ isActive }) =>
                  `text-sm transition-colors hover:text-[#6FE3C1] ${isActive ? "text-[#6FE3C1]" : "text-[#D9E1E8]"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block">
            <Button asChild className="bg-[#2EC4A6] text-[#071A2C] hover:bg-[#6FE3C1]" data-testid="header-book-demo-button">
              <Link to="/contact">Book a Demo</Link>
            </Button>
          </div>

          <button
            type="button"
            aria-label="Toggle navigation"
            data-testid="header-mobile-menu-button"
            className="rounded-md border border-white/20 p-2 md:hidden"
            onClick={() => setIsOpen((current) => !current)}
          >
            {isOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="glass-panel mx-6 mb-4 rounded-xl p-4 md:hidden"
            data-testid="header-mobile-menu-panel"
          >
            <div className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <NavLink
                  key={link.id}
                  to={link.to}
                  data-testid={`mobile-nav-${link.id}-link`}
                  className={`rounded-md px-3 py-2 text-sm ${pathname === link.to ? "bg-[#2EC4A6]/20 text-[#6FE3C1]" : "text-[#D9E1E8]"
                    }`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </NavLink>
              ))}
              <Button asChild className="mt-2 bg-[#2EC4A6] text-[#071A2C] hover:bg-[#6FE3C1]" data-testid="mobile-book-demo-button">
                <Link to="/contact" onClick={() => setIsOpen(false)}>
                  Book a Demo
                </Link>
              </Button>
            </div>
          </motion.div>
        )}
      </header>

      <main className="relative z-10">{children}</main>

      <footer className="border-t border-white/10 py-12" data-testid="site-footer">
        <div className="mx-auto max-w-7xl space-y-10 px-6 md:px-10">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4" data-testid="footer-navigation-grid">
            {footerSections.map((section, sectionIndex) => (
              <div key={section.title} className="space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-[#6FE3C1]" data-testid={`footer-section-${sectionIndex + 1}-title`}>
                  {section.title}
                </h3>
                <ul className="space-y-2 text-sm text-[#D9E1E8]/80">
                  {section.links.map((link, linkIndex) => (
                    <li key={link.label}>
                      <Link
                        to={link.to}
                        className="transition-colors hover:text-[#6FE3C1]"
                        data-testid={`footer-${section.title.toLowerCase()}-link-${linkIndex + 1}`}
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          {/* Contact Section */}

          <div className="border-t border-white/10 pt-6 mt-4 text-sm text-[#D9E1E8]/80">

            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">

              {/* Contact Info */}

              <div className="space-y-2">

                <div>
                  📍 6th floor, Hiranandani Business Park, Saki Vihar Rd,
                  Powai, Mumbai Maharashtra 400072
                </div>

                <div>
                  ✉ admin@theaimatters.com
                </div>

                <div>
                  📞 +91 8000710981
                </div>

              </div>

              {/* Social Icons */}

              <div className="flex gap-10">

                <a
                  href="https://www.linkedin.com/company/the-ai-matters/"
                  target="_blank"
                  className="flex flex-col items-center hover:text-[#6FE3C1]"
                >
                  <Linkedin size={22} />
                  <span className="text-sm mt-1">LinkedIn</span>
                </a>

                <a
                  href="https://www.instagram.com/theaimatters"
                  target="_blank"
                  className="flex flex-col items-center hover:text-[#6FE3C1]"
                >
                  <Instagram size={22} />
                  <span className="text-sm mt-1">Instagram</span>
                </a>

                <a
                  href="https://wa.me/918000710981"
                  target="_blank"
                  className="flex flex-col items-center hover:text-[#6FE3C1]"
                >
                  <Phone size={22} />
                  <span className="text-sm mt-1">WhatsApp</span>
                </a>

              </div>

            </div>

          </div>
          <div className="flex flex-col gap-3 border-t border-white/10 pt-6 text-sm text-[#D9E1E8]/70 md:flex-row md:items-center md:justify-between">
            <p data-testid="footer-copyright-text">© {new Date().getFullYear()} The AI Matters. All rights reserved.</p>
            <p data-testid="footer-tagline-text">AI-powered hiring infrastructure for modern companies.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
